import { useEffect, useRef, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '../../hooks/useAuth';
import { climbTitle } from '../../utils/climb-title';
import type { Climb } from '../../types/climb';
import { ClimbStandingIcon } from './ClimbStandingIcon';

interface ClimbsListDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Shown when the list is empty. */
  emptyText: string;
  climbs: Climb[];
  loading?: boolean;
  /** The climb on screen, highlighted and scrolled to. */
  currentSlug: string;
  /** A climb picked: the list as shown and its place in it, for the swipe. */
  onPick: (climb: Climb, climbs: Climb[], index: number) => void;
  /** Under the rows: what loads the next page, for a list that pages. */
  footer?: ReactNode;
}

/**
 * The list a climb was opened from, from the climb's own page, one line each —
 * name, grade and where the reader stands on it — to jump to another without
 * going back to the list and finding the place again. A drawer from the foot
 * of the screen, opened from the climb's name in the breadcrumb.
 *
 * Only draws: where the rows come from is the caller's (the board's endpoint in
 * BoardClimbsListDrawer, the list saved on the way in for the rest).
 */
export default function ClimbsListDrawer({
  open,
  onOpenChange,
  title,
  description,
  emptyText,
  climbs,
  loading = false,
  currentSlug,
  onPick,
  footer,
}: ClimbsListDrawerProps) {
  const { user } = useAuth();
  const currentRowRef = useRef<HTMLButtonElement | null>(null);

  // Once the rows are in, the climb on screen is brought into the middle of
  // the list rather than left wherever the top of it put it.
  useEffect(() => {
    if (open && !loading) currentRowRef.current?.scrollIntoView?.({ block: 'center' });
  }, [open, loading]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="flex max-h-[85vh] flex-col gap-3 p-0">
        <SheetHeader className="px-6 pt-6">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto border-t" data-testid="climbs-list">
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : climbs.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">{emptyText}</p>
          ) : (
            <ul>
              {climbs.map((climb, index) => {
                const current = climb.slug === currentSlug;
                return (
                  <li key={climb.id} className="border-b last:border-b-0">
                    <button
                      type="button"
                      ref={current ? currentRowRef : undefined}
                      aria-current={current ? 'true' : undefined}
                      data-testid="climbs-list-row"
                      onClick={() => onPick(climb, climbs, index)}
                      className={`flex w-full items-center gap-3 px-6 py-2.5 text-left transition-colors ${
                        current ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-muted/60'
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate">{climbTitle(climb)}</span>
                      <span className="shrink-0 text-sm tabular-nums">
                        {(climb.grade_visible && climb.difficulty) || '—'}
                      </span>
                      {/* Nothing for a visitor, who stands nowhere on anything. */}
                      {user && <ClimbStandingIcon climb={climb} className="size-4" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && footer}
        </div>
      </SheetContent>
    </Sheet>
  );
}
